import * as Yup from "yup"
import {
	einRegex,
	usCountryCodeRegex,
	intlCountryCodeRegex,
	usPhoneNumberRegex,
	intlPhoneNumberRegex,
	flexibleUrlRegex,
	yearOfIncorporation,
} from "../../../../utilities/validation"
import {CA_COUNTRY_CODE, caPostalCode, US_COUNTRY_CODE, usPostalCode} from "../../../../resources/address"
import {
	BusinessAnnualRevenue,
	BusinessNumberOfEmployees,
	BusinessVertical,
	CashFlow,
	IndustryType,
} from "../../../../resources/application"
import {address, ein, phone, website, name, operatingAddressSchema} from "../../../../utilities/validation"

const validationErrorMessages = {
	name: name,
	address: address,
	phone: phone,
	website: website,
	dba: {invalid: "DBA must be 128 characters or less"},
	ein: {
		...ein,
		required: "Please enter the EIN",
	},
	businessVertical: {invalid: "Invalid business vertical selected", required: "Please select a business vertical"},
	yearOfIncorporation: yearOfIncorporation,
	numberOfEmployees: {invalid: "Invalid number of employees selected"},
	annualRevenue: {invalid: "Invalid annual revenue selected"},
	cashFlow: {invalid: "Invalid cash flow selected"},
	industry: {invalid: "Invalid industry selected"},
	stateOfIncorporation: {required: "Please enter the state of incorporation"},
}

export default {
	name: Yup.string().required(validationErrorMessages.name.required),
	address: Yup.object({
		street: Yup.string().required(validationErrorMessages.address.street.required),
		city: Yup.string().required(validationErrorMessages.address.city.required),
		state: Yup.string().when("country", {
			is: (country: string) => [US_COUNTRY_CODE, CA_COUNTRY_CODE].includes(country),
			then: Yup.string().required(validationErrorMessages.address.state.required),
		}),
		postalCode: Yup.string()
			.required(validationErrorMessages.address.postalCode.required)
			.when("country", {
				is: US_COUNTRY_CODE,
				then: Yup.string().matches(usPostalCode, validationErrorMessages.address.postalCode.usInvalid),
			})
			.when("country", {
				is: CA_COUNTRY_CODE,
				then: Yup.string().matches(caPostalCode, validationErrorMessages.address.postalCode.caInvalid),
			}),
		country: Yup.string().required(validationErrorMessages.address.country.required),
	}),
	dba: Yup.string().max(128, validationErrorMessages.dba.invalid),
	ein: Yup.string()
		.matches(einRegex, validationErrorMessages.ein.invalid)
		.required(validationErrorMessages.ein.required),
	phone: Yup.object({
		countryCode: Yup.string()
			.required(validationErrorMessages.phone.countryCode.required)
			.matches(
				new RegExp(`${usCountryCodeRegex.source}|${intlCountryCodeRegex.source}`),
				validationErrorMessages.phone.countryCode.required
			),
		number: Yup.string()
			.required(validationErrorMessages.phone.number.required)
			.when("countryCode", {
				is: (value: string) => usCountryCodeRegex.test(value),
				then: Yup.string().matches(usPhoneNumberRegex, validationErrorMessages.phone.number.usInvalid),
			})
			.when("countryCode", {
				is: (value: string) => intlCountryCodeRegex.test(value),
				then: Yup.string().matches(intlPhoneNumberRegex, validationErrorMessages.phone.number.intlInvalid),
			}),
	}),
	website: Yup.string()
		.nullable()
		.when([], {
			is: () => true,
			then: Yup.string()
				.nullable()
				.test("website-required", validationErrorMessages.website.required, function (value) {
					// When website is null (no website checkbox checked), skip validation
					if (value === null) return true
					// Otherwise, website is required
					return !!value && value.length > 0
				})
				.test("website-format", validationErrorMessages.website.invalid, function (value) {
					if (value === null || value === undefined || value === "") return true
					return flexibleUrlRegex.test(value)
				}),
		}),
	businessVertical: Yup.string().oneOf(Object.keys(BusinessVertical), validationErrorMessages.businessVertical.invalid),
	yearOfIncorporation: Yup.string().matches(/^\d{4}$/, validationErrorMessages.yearOfIncorporation.invalid),
	numberOfEmployees: Yup.string().oneOf(
		Object.keys(BusinessNumberOfEmployees),
		validationErrorMessages.numberOfEmployees.invalid
	),
	annualRevenue: Yup.string().oneOf(Object.keys(BusinessAnnualRevenue), validationErrorMessages.annualRevenue.invalid),
	cashFlow: Yup.string().oneOf(Object.keys(CashFlow), validationErrorMessages.cashFlow.invalid),
	industry: Yup.string().oneOf(Object.keys(IndustryType), validationErrorMessages.industry.invalid),
	stateOfIncorporation: Yup.string().required(validationErrorMessages.stateOfIncorporation.required),
	countriesOfOperation: Yup.array().of(Yup.string()),
	operatingAddress: operatingAddressSchema,
}
